import { useState } from 'react';
import { Flag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { reportsAPI } from '../lib/api';

/**
 * Reusable report flow. Drops in anywhere — feed posts, comments, messages, profiles.
 *
 * Props:
 *   contentType: 'post' | 'comment' | 'message' | 'user' | 'job' | 'marketplace' | 'provider'
 *   contentId:   id of the thing being reported
 *   authorId:    optional — the user who authored the reported content (helps route signals)
 *   variant:     'icon' (default, small flag button) | 'menu' (text row for dropdown menus)
 *   onReported:  optional callback
 */
/**
 * Controlled dialog — use when the trigger lives elsewhere (e.g. inside a DropdownMenuItem).
 * Render once at page level; set `target={{contentType, contentId, authorId}}` to open.
 */
export function ReportDialog({ target, onClose, onReported }) {
  const open = !!target;
  return (
    <ReportButtonInner
      contentType={target?.contentType}
      contentId={target?.contentId}
      authorId={target?.authorId}
      controlledOpen={open}
      onOpenChange={(v) => { if (!v) onClose?.(); }}
      hideTrigger
      onReported={onReported}
    />
  );
}

function ReportButtonInner({ contentType, contentId, authorId, variant = 'icon', onReported, controlledOpen, onOpenChange, hideTrigger }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (controlledOpen !== undefined) onOpenChange?.(v);
    else setInternalOpen(v);
  };
  return <ReportInner contentType={contentType} contentId={contentId} authorId={authorId} variant={variant} onReported={onReported} open={open} setOpen={setOpen} hideTrigger={hideTrigger} />;
}

export default function ReportButton({ contentType, contentId, authorId, variant = 'icon', onReported }) {
  return <ReportButtonInner contentType={contentType} contentId={contentId} authorId={authorId} variant={variant} onReported={onReported} />;
}

function ReportInner({ contentType, contentId, authorId, variant, onReported, open, setOpen, hideTrigger }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const REASONS = [
    { value: 'spam', label: 'Spam or scam' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'sexual', label: 'Unwanted sexual content' },
    { value: 'hate', label: 'Hate speech or discrimination' },
    { value: 'violence', label: 'Violence or threats' },
    { value: 'fake', label: 'Fake account / not an alumnus' },
    { value: 'other', label: 'Something else' },
  ];

  const submit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await reportsAPI.create({
        content_type: contentType,
        content_id: contentId,
        author_id: authorId,
        reason,
        details,
      });
      setSubmitted(true);
      if (onReported) onReported();
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setReason('');
        setDetails('');
      }, 1400);
    } catch (e) {
      // best effort
    } finally {
      setSubmitting(false);
    }
  };

  const trigger = variant === 'menu' ? (
    <button
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
    >
      <Flag className="w-4 h-4" /> Report
    </button>
  ) : (
    <button
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      className="text-slate-400 hover:text-red-500 transition-colors"
      title="Report"
    >
      <Flag className="w-4 h-4" />
    </button>
  );

  return (
    <>
      {!hideTrigger && trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report this {contentType === 'user' ? 'member' : contentType}</DialogTitle>
          </DialogHeader>
          {submitted ? (
            <div className="py-6 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Flag className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="font-semibold text-dark-slate">Thanks — we'll take a look.</p>
              <p className="text-sm text-slate-gray mt-1">Our Trust &amp; Safety team reviews every report.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 py-2">
                <label className="text-sm font-medium text-dark-slate">Why are you reporting this?</label>
                <div className="space-y-1">
                  {REASONS.map((r) => (
                    <label key={r.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${reason === r.value ? 'border-mint-fresh bg-mint-fresh/5' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <input
                        type="radio"
                        name="report-reason"
                        value={r.value}
                        checked={reason === r.value}
                        onChange={() => setReason(r.value)}
                        className="accent-mint-fresh"
                      />
                      <span className="text-sm text-dark-slate">{r.label}</span>
                    </label>
                  ))}
                </div>
                <Textarea
                  placeholder="Add context (optional)"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className="mt-2 min-h-[80px]"
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={!reason || submitting} className="bg-dark-slate hover:bg-dark-slate/90 text-white">
                  {submitting ? 'Submitting…' : 'Submit report'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
